import { type ReactElement } from 'react';
import { landingRoute, refusalReasonFor } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { Button, PageHeader, Stack, Measure, Text } from '@vello/web-theme';
import { NavIcon } from '@vello/web-theme';

/**
 * The refused screen (W2-CONSOLES, D364). Before this, a persona that reached a screen its grant does not open saw
 * whatever raw code the server answered (`403`, `FORBIDDEN`) inside a red notice, or an empty table that looked
 * like "no data". This says, in the operator's own language, that the access does not include the section — and
 * offers the way out: a screen its grant DOES open.
 *
 * It distinguishes the two refusals the client can tell apart: a word the grant does not carry at all, and a word
 * it carries somewhere else (the wrong market is selected). No fabricated content, one primary action.
 */
export function AccessDeniedScreen({ route, code = 'FORBIDDEN' }: { route: string; code?: string }): ReactElement {
  const { grants } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();
  const reason = refusalReasonFor(code, grants, route);
  const scoped = reason === 'out_of_scope';
  const target = landingRoute(grants);

  return (
    <>
      {/* D854 (L7a-015): the sentence is said once, in the panel with its icon and its button, all centered together. */}
      <PageHeader title={t(scoped ? 'access.scope.title' : 'access.denied.title')} />
      <Stack gap={2} align="center" className="vello-placeholder">
        <span className="vello-empty__mark vello-admin-a-bigmark">
          <NavIcon name="shield" size={22} />
        </span>
        <Measure size="md"><Text as="div" role="caption">{t(scoped ? 'access.scope.body' : 'access.denied.body')}</Text></Measure>
        {target !== route && (
          <Button variant="primary" onClick={() => navigate(target)}>{t('access.back')}</Button>
        )}
      </Stack>
    </>
  );
}
